import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, gql } from '@apollo/client';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import GoogleLoginButton from '../components/GoogleLoginButton';

const LOGIN_MUTATION = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      success
      message
      token
      user {
        id
        username
        email
        isSemiAdmin
        isVerified
      }
      errors {
        field
        message
      }
    }
  }
`;

const Login = () => {
  const [login, { loading }] = useMutation(LOGIN_MUTATION);
  const { login: authLogin, setError, clearError } = useAuth();
  const navigate = useNavigate();
  const [formErrors, setFormErrors] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const onSubmit = async (data) => {
    console.log('Login form submitted:', { email: data.email });
    clearError();
    setFormErrors([]);

    try {
      const response = await login({
        variables: {
          input: {
            email: data.email,
            password: data.password
          }
        }
      });

      const result = response.data.login;

      if (result.success) {
        console.log('Login successful:', result.user.username);
        authLogin({
          user: result.user,
          token: result.token
        });
        navigate('/profile');
      } else {
        console.log('Login failed:', result.message);
        setFormErrors(result.errors || [{ field: 'general', message: result.message }]);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('登入過程發生錯誤，請稍後再試');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            登入帳號
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            或{' '}
            <Link
              to="/register"
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              註冊新帳號
            </Link>
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {/* Google 登入按鈕 */}
          <div>
            <GoogleLoginButton text="使用 Google 登入" variant="default" />
          </div>

          {/* 分隔線 */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-50 text-gray-500">或使用郵箱密碼登入</span>
            </div>
          </div>
        </div>

        <form className="mt-6 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                email
              </label>
              <input
                {...register('email', {
                  required: '請輸入email',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: '請輸入有效的email'
                  }
                })}
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                密碼
              </label>
              <input
                {...register('password', {
                  required: '請輸入密碼',
                  minLength: {
                    value: 6,
                    message: '密碼至少需要6個字符'
                  }
                })}
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="密碼"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>
          </div>

          {formErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              {formErrors.map((error, index) => (
                <p key={index} className="text-sm text-red-600">
                  {error.message}
                </p>
              ))}
            </div>
          )}

          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              className="w-full"
            >
              {loading ? '登入中...' : '登入'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login; 